import {ISegmentationAggregatorService} from './segmentation-aggregator.service.interface';
import {Check} from '@sparkware/app-utils';
import {ServiceBase} from '../ServiceBase';
import {IServiceFactoryExtended} from '../service-factory-extended.interface';
import { action, observable, makeObservable } from 'mobx';
import {mediaQueries} from "../media-query/media-queries";
import {IMediaQueries} from '../media-query/media-queries.interface';

export class SegmentationAggregatorService extends ServiceBase implements ISegmentationAggregatorService {
  constructor(serviceFactory: IServiceFactoryExtended) {
    super(serviceFactory);

    makeObservable(this);

    this._subscribeReactions();
  }

  private async _subscribeReactions(): Promise<void> {
    const cmsSegmentation = await this.services.cmsSegmentation;
    const progressiveWebApp = await this.services.progressiveWebApp;
    this.reaction(
        () => {
          return {
            userSegments: this.services.user.segments,
            cmsSegments: cmsSegmentation.segments,
            deviceSegments: this.services.device.segments,
            isMobile: this.services.device.isMobile,
            mediaQueryChange: this.services.mediaQuery.lastMediaChange,
            isMiniOrbit: this.services.miniOrbit.isEnabled,
            isPWAInstallable: progressiveWebApp.isPWAInstallable,
            videoSlidesEnabled: this.services.videoSlides.shouldBeDisplayed,
          }
        },
        () => {
          this._updateSegments();
        },
        {
          fireImmediately: true
        });
  }

  private async _updateSegments(): Promise<void> {
    const cmsSegmentation = await this.services.cmsSegmentation;
    const extraSegments = await this._getExtraSegments();
    this._setAllSegments({
      ...cmsSegmentation.segments,
      ...this.services.user.segments,
      ...this.services.device.segments,
      ...this._getMediaQuerySegments,
      ...extraSegments});
  }

  @observable.ref
  private _allSegments: Record<string, any> = {};

  @observable.ref
  segmentsLastUpdate: number = null;


  @action.bound
  private _setAllSegments(allSegments) {
    this._allSegments = allSegments;
    this.segmentsLastUpdate = Date.now();
  }

  getSegmentValue(name: string): any {
    let segmentValue = this._allSegments[name];

    if(Check.isUndefined(segmentValue)) {
      this.services.logger.debug(`Segment ${name} doesn't exists in the segments list!`);
    }

    return segmentValue;
  }

  private get _getMediaQuerySegments() {
    const tempRecords = {};
    (Object.keys(mediaQueries) as (keyof IMediaQueries)[]).forEach(mediaQueryName => {
      tempRecords[mediaQueryName] = this.services.mediaQuery[mediaQueryName];
    });
    return tempRecords;
  }

  private async _getExtraSegments() {
    const progressiveWebApp = await this.services.progressiveWebApp;
    return {
      env: this.services.configuration.startupConfiguration.sdk.config.ExtraProperties.env,
      countryCode: this.services.configuration.startupConfiguration.country,
      brandID: this.services.sdk.getBrandID(),
      subBrandID: this.services.sdk.getSubBrandID(),
      regulationID: this.services.regulation.getRegulationCode(),
      iso3: this.services.language.currentLanguage,
      langCode: this.services.language.currentLanguage,
      isMiniOrbit: this.services.miniOrbit.isEnabled,
      isPWAInstallable: progressiveWebApp.isPWAInstallable,
      videoSlidesEnabled: this.services.videoSlides.shouldBeDisplayed,
    };
  }

  get isCIS(): boolean {
    return Boolean(this.getSegmentValue('_IsCIS'));
  }

  get isCIP(): boolean {
    return Boolean(this.getSegmentValue('_IsCIP'));
  }

}
